import {
	differenceInCalendarDays,
	eachMonthOfInterval,
	endOfMonth,
	format,
	parse,
	startOfMonth,
} from "date-fns";
import { ptBR } from "date-fns/locale";

export function parseScheduleDate(value: string | undefined): Date | undefined {
	if (!value) return undefined;
	const parsed = parse(value, "yyyy-MM-dd", new Date());
	return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

export interface GanttDomain {
	start: Date;
	end: Date;
	totalDays: number;
}

export interface GanttMonth {
	key: string;
	label: string;
	offsetFraction: number;
	widthFraction: number;
}

export interface GanttBar {
	leftFraction: number;
	widthFraction: number;
}

export function buildGanttDomain(
	dates: (string | undefined)[],
): GanttDomain | null {
	const times = dates
		.map(parseScheduleDate)
		.filter((date): date is Date => date !== undefined)
		.map((date) => date.getTime());
	if (times.length === 0) return null;

	const start = startOfMonth(new Date(Math.min(...times)));
	const end = endOfMonth(new Date(Math.max(...times)));
	const totalDays = differenceInCalendarDays(end, start) + 1;
	return { start, end, totalDays };
}

export function ganttMonths(domain: GanttDomain): GanttMonth[] {
	return eachMonthOfInterval({ start: domain.start, end: domain.end }).map(
		(monthStart) => {
			const offset = differenceInCalendarDays(monthStart, domain.start);
			const width =
				differenceInCalendarDays(endOfMonth(monthStart), monthStart) + 1;
			return {
				key: format(monthStart, "yyyy-MM"),
				label: format(monthStart, "MMM/yy", { locale: ptBR }),
				offsetFraction: offset / domain.totalDays,
				widthFraction: width / domain.totalDays,
			};
		},
	);
}

export function ganttBar(
	domain: GanttDomain,
	start: string | undefined,
	end: string | undefined,
): GanttBar | null {
	const startDate = parseScheduleDate(start);
	const endDate = parseScheduleDate(end);
	if (!startDate || !endDate) return null;

	const startOffset = Math.max(
		0,
		differenceInCalendarDays(startDate, domain.start),
	);
	const endOffset = Math.min(
		domain.totalDays,
		differenceInCalendarDays(endDate, domain.start) + 1,
	);
	const width = Math.max(0, endOffset - startOffset);
	return {
		leftFraction: startOffset / domain.totalDays,
		widthFraction: width / domain.totalDays,
	};
}

export function formatScheduleRange(
	start: string | undefined,
	end: string | undefined,
): string | null {
	const startDate = parseScheduleDate(start);
	const endDate = parseScheduleDate(end);
	if (!startDate || !endDate) return null;
	return `${format(startDate, "dd/MM/yy")} – ${format(endDate, "dd/MM/yy")}`;
}

export function scheduleDays(
	start: string | undefined,
	end: string | undefined,
): number | null {
	const startDate = parseScheduleDate(start);
	const endDate = parseScheduleDate(end);
	if (!startDate || !endDate) return null;
	return differenceInCalendarDays(endDate, startDate) + 1;
}

export function formatDays(days: number): string {
	return days === 1 ? "1 dia" : `${days} dias`;
}

export function formatScheduleDuration(
	start: string | undefined,
	end: string | undefined,
): string | null {
	const days = scheduleDays(start, end);
	return days === null ? null : formatDays(days);
}

export function formatScheduleTotal(
	start: string | undefined,
	end: string | undefined,
): string | null {
	const days = scheduleDays(start, end);
	if (days === null) return null;
	return days === 1 ? "1 dia total" : `${days} dias totais`;
}

export function formatActualRange(
	start: string | undefined,
	end: string | undefined,
): string {
	const startDate = parseScheduleDate(start);
	if (!startDate) return "Realizado: —";
	const endDate = parseScheduleDate(end);
	const from = format(startDate, "dd/MM/yy");
	return endDate
		? `Realizado: ${from} – ${format(endDate, "dd/MM/yy")}`
		: `Realizado: ${from} – em andamento`;
}

export function formatDurationDelta(delta: number): string {
	if (delta === 0) return "no prazo";
	return delta > 0 ? `+${formatDays(delta)}` : `−${formatDays(-delta)}`;
}
